import React, { useState } from 'react';
import { useInvoiceActions } from '@dopomogai/agent-kit';
import { Button, Badge, Tooltip, Icon, Alert, Spinner } from '@dopomogai/ui';
import type { InvoiceLog } from '@dopomogai/api-client';

interface InvoiceListItemProps {
  invoice: InvoiceLog & { metadata?: any };
  onActionSuccess: () => void;
  onDuplicate: () => void;
}

const getStatusColor = (status: string): 'green' | 'yellow' | 'red' | 'gray' => {
  switch (status) {
    case 'success': return 'green';
    case 'created': case 'processing': return 'yellow';
    case 'failure': case 'invalidated': case 'expired': return 'red';
    default: return 'gray';
  }
};

export const InvoiceListItem: React.FC<InvoiceListItemProps> = ({ invoice, onActionSuccess, onDuplicate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const { checkStatus, invalidate, isCheckingStatus, isInvalidating, error, clearError } = useInvoiceActions();

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!invoice.page_url) return;
    navigator.clipboard.writeText(invoice.page_url);
    setIsLinkCopied(true);
    setTimeout(() => setIsLinkCopied(false), 2500);
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  }

  const handleCheckStatus = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const status = await checkStatus(invoice.provider_invoice_id);
    if (status) {
      alert(`Status for ${invoice.provider_invoice_id} is now: ${status.status}. The list will now refresh.`);
      onActionSuccess();
    }
  };

  const handleInvalidate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to invalidate invoice ${invoice.provider_invoice_id}? This cannot be undone.`)) {
      const success = await invalidate(invoice.provider_invoice_id);
      if (success) {
        alert('Invoice invalidated successfully. The list will now refresh.');
        onActionSuccess();
      }
    }
  };

  const hasDetails = invoice.metadata && (invoice.metadata.destination || invoice.metadata.comment || invoice.metadata.items);

  return (
    <>
      <div 
        className="grid grid-cols-12 gap-4 items-center p-3 border-b border-slate-200 hover:bg-slate-50 text-sm"
        onClick={() => hasDetails && setIsExpanded(p => !p)}
        style={{ cursor: hasDetails ? 'pointer' : 'default' }}
      >
        <div className="col-span-1 flex justify-center items-center text-slate-400">
          {hasDetails && <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} />}
        </div>
        <div className="col-span-5">
            <p className="font-medium text-slate-800 truncate" title={invoice.metadata?.destination}>
              {invoice.metadata?.destination || 'No destination provided'}
            </p>
            <p className="font-mono text-xs text-slate-500 truncate">{invoice.provider_invoice_id}</p>
        </div>
        <div className="col-span-2">
          <Badge text={invoice.status} accentColor={getStatusColor(invoice.status)} />
        </div>
        <div className="col-span-2 font-semibold">
          {invoice.amount != null ? `${Number(invoice.amount).toFixed(2)} ${invoice.currency}` : 'N/A'}
        </div>
        
        <div className="col-span-2 flex gap-1 justify-end">
          {invoice.page_url && (invoice.status === 'created' || invoice.status === 'processing') && (
            <Tooltip text={isLinkCopied ? "Link Copied!" : "Copy Payment Link"}>
              <Button size="sm" variant="outline" icon={<Icon name={isLinkCopied ? "Check" : "Link"} />} onClick={handleCopyLink} disabled={isCheckingStatus || isInvalidating} />
            </Tooltip>
          )}
          <Tooltip text="Duplicate Invoice">
            <Button size="sm" variant="outline" icon={<Icon name="Copy" />} onClick={(e) => handleActionClick(e, onDuplicate)} disabled={isCheckingStatus || isInvalidating} />
          </Tooltip>
          <Tooltip text="Refresh status from Monobank">
            <Button size="sm" variant="outline" icon={isCheckingStatus ? <Spinner size="sm" /> : <Icon name="RefreshCw" />} onClick={handleCheckStatus} disabled={isCheckingStatus || isInvalidating} />
          </Tooltip>
          <Tooltip text="Invalidate this unpaid invoice">
            <Button size="sm" variant="danger" icon={isInvalidating ? <Spinner size="sm" /> : <Icon name="XCircle" />} onClick={handleInvalidate} disabled={isCheckingStatus || isInvalidating || invoice.status !== 'created'} />
          </Tooltip>
        </div>
      </div>
      
      {isExpanded && hasDetails && (
        <div className="p-4 bg-slate-50 border-b border-slate-200 animate-fade-in">
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div className="md:col-span-3">
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">Destination (Client-Visible)</p>
              <p className="p-2 bg-white rounded border">{invoice.metadata.destination || <span className="text-slate-400">Not provided.</span>}</p>
            </div>
            {invoice.metadata.comment && (
              <div className="md:col-span-3">
                <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">Internal Comment</p>
                <p className="p-2 bg-white rounded border text-slate-600 italic">{invoice.metadata.comment}</p>
              </div>
            )}
            <div className="md:col-span-3">
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">Itemized List</p>
              <div className="border bg-white rounded-md divide-y divide-slate-200">
                <div className="grid grid-cols-6 gap-2 font-semibold p-2 bg-slate-50 text-xs">
                  <div className="col-span-3">Item</div>
                  <div className="text-center">Qty</div>
                  <div className="text-right">Unit Price</div>
                  <div className="text-right">Subtotal</div>
                </div>
                {invoice.metadata.items?.map((item: any, index: number) => (
                  <div key={index} className="p-2 grid grid-cols-6 gap-2">
                    <div className="col-span-3 font-medium">{item.name}</div>
                    <div className="text-center">{item.quantity} {item.unit}</div>
                    <div className="text-right">{Number(item.amount).toFixed(2)}</div>
                    <div className="text-right font-semibold">{(item.quantity * Number(item.amount)).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {error && <Alert type="error" message={error.message} className="m-2" onClose={clearError} isVisible={!!error} />}
    </>
  );
};